import "./NotificationBell.css";

import { forwardRef, HTMLAttributes, useState } from "react";

import { NotificationBellIcon } from "../../../assets/icons";
import { NotificationPanel } from "../../../templates/NotificationPanel";

// The forwarded ref lets NavigationTooltip reach the bell without the deprecated findDOMNode, and the mouse
// handlers let it show on hover. On the button, not the wrapper, so hovering the open panel does not raise it.
type TooltipTriggerProps = Pick<HTMLAttributes<HTMLButtonElement>, "onMouseEnter" | "onMouseLeave">;

interface NotificationBellProps extends TooltipTriggerProps {
  unreadCount: number;
  variant: "seed" | "hub";
  ariaLabel?: string;
}

const getBadgeLabel = (count: number): string => (count >= 10 ? "9+" : String(count));

export const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(
  ({ unreadCount, variant, ariaLabel, onMouseEnter, onMouseLeave }, ref): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);

    const computedAriaLabel = ariaLabel ?? (unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications");

    return (
      <div className="NotificationBell_wrapper">
        <button
          ref={ref}
          type="button"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={`NotificationBell NotificationBell_${variant}${isOpen ? " NotificationBell_open" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={computedAriaLabel}
          aria-expanded={isOpen}
        >
          <NotificationBellIcon aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="NotificationBell_badge" aria-hidden="true">
              {getBadgeLabel(unreadCount)}
            </span>
          )}
        </button>
        <NotificationPanel show={isOpen} onClose={() => setIsOpen(false)} variant={variant} />
      </div>
    );
  }
);

NotificationBell.displayName = "NotificationBell";
